import { z } from "zod/v4";

import { CreateAudienceDtoSchema } from "../../../dtos/audiences";
import { CreateExclusionGroupOnCreateSchema } from "../../../dtos/exclusion-group";
import { CreateExperienceCustomMetricDtoSchema } from "../../../dtos/experience-custom-metrics";
import { CreateExperienceKeyMetricDtoSchema } from "../../../dtos/experience-key-metrics";
import { CreateExperiencePageTargetingDtoSchema } from "../../../dtos/experience-page-targeting";
import {
  CreateExperienceDtoSchema,
  ExperienceDtoSchema,
} from "../../../dtos/experiences";
import { OfferProductDtoSchema } from "../../../dtos/offer-products";
import { OfferDtoSchema } from "../../../dtos/offers";
import { OnsiteEditDtoSchema } from "../../../dtos/onsite-edits";
import { CreateOnsiteInjectionDtoSchema } from "../../../dtos/onsite-injections";
import { ProductVariantProductTypeList } from "../../../dtos/product-variants";
import { RedirectDtoSchema } from "../../../dtos/redirects";
import { S3FileLocationDtoSchema } from "../../../dtos/s3-file-location";
import { UserInterfaceDtoSchema } from "../../../dtos/user-interfaces";
import { CreateVariationDtoSchema } from "../../../dtos/variations";

export const PutExperienceRequestBodySchema = z.object({
  experienceId: z
    .uuid()
    .optional()
    .describe(
      "UUID of existing experience to update (optional for new experiences)"
    ),
  experience: CreateExperienceDtoSchema.extend({
    productsLocation: S3FileLocationDtoSchema.nullish(),
    productType: z.enum(ProductVariantProductTypeList).nullish(),
  }).describe("Core experience configuration and settings"),
  variations: CreateVariationDtoSchema.array().describe(
    "Array of test variations (minimum 2: control + variant)"
  ),
  onsiteEdits: OnsiteEditDtoSchema.array()
    .optional()
    .default([])
    .describe("Content modifications to apply on-site (text, HTML, images)"),
  onsiteInjections: CreateOnsiteInjectionDtoSchema.array()
    .optional()
    .default([])
    .describe("Custom CSS/JavaScript to inject into pages"),
  redirects: RedirectDtoSchema.array()
    .optional()
    .default([])
    .describe("URL redirects for split testing different pages"),
  audience: CreateAudienceDtoSchema.optional().describe(
    "Targeting rules to determine who sees the experience"
  ),
  exclusionGroup: CreateExclusionGroupOnCreateSchema.optional().describe(
    "Mutual exclusion group to prevent users from seeing multiple experiences"
  ),
  experiencePageTargeting: CreateExperiencePageTargetingDtoSchema.array()
    .optional()
    .default([])
    .describe("Page-level targeting rules for where the experience should run"),
  offerProducts: OfferProductDtoSchema.array()
    .optional()
    .default([])
    .describe("Products to include/exclude from pricing offers"),
  offerProductsKey: z
    .string()
    .optional()
    .describe("Key for bulk product selection"),
  offers: OfferDtoSchema.array()
    .optional()
    .default([])
    .describe("Discount/pricing offers to test"),
  userInterfaces: UserInterfaceDtoSchema.array()
    .optional()
    .default([])
    .describe("UI widgets to display (banners, modals, etc.)"),
  experienceKeyMetrics: CreateExperienceKeyMetricDtoSchema.array()
    .default([])
    .describe("Key business metrics to track (revenue, conversion, AOV)"),
  experienceCustomMetrics: CreateExperienceCustomMetricDtoSchema.array()
    .default([])
    .optional()
    .describe("Custom event metrics to track"),
});

export const PutExperienceParamsSchema = z.object({
  experienceId: z.string().optional(),
});

export type PutExperienceParamsType = z.infer<typeof PutExperienceParamsSchema>;

export type PutExperienceRequestBodyType = z.infer<
  typeof PutExperienceRequestBodySchema
>;

export const PutExperienceResponseBodySchema = z.object({
  experience: ExperienceDtoSchema,
});

export type PutExperienceResponseBodyType = z.infer<
  typeof PutExperienceResponseBodySchema
>;
